import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';

import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { updateUser } from '../api';
import { type User, VerificationLevel } from '../types';
import { isUserVerified } from '../user-verification';
import { clientName, formatShortDate, initialsOf } from '../utils';
import { Chip, VerificationChip } from './verification-badge';
import { ConfirmationModal } from './confirmation-modal';
import { UserAvatar } from './user-avatar';

/**
 * Head of the «Клієнт» screen — `Redesign-D-Admin{,-Mobile}`.
 *
 * «Написати» (a bare `mailto:` to the client) was removed (owner, 2026-10-12):
 * it only opened the operator's mail app and recorded nothing.
 *
 * The artboard packs identity, the three status pills and the two operator
 * actions into one band, so the answer to "who is this and what can I do about
 * them" is above the fold on both viewports. The second action is drawn as a
 * destructive outline; there is no blocking endpoint on this API, so the slot
 * carries the destructive action that does exist — removing verification.
 * Further page-level actions (account deletion) arrive through `actions`, so
 * the header stays free of routing.
 */
export function UserProfileHeader({ user, actions }: { user: User; actions?: ReactNode }) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const name = clientName(user) || t('clients.detail.noName');
  const verified = isUserVerified(user);
  const level = user.verificationLevel ?? VerificationLevel.BASIC;
  const role = user.role?.[0] ?? 'user';

  const mutation = useMutation({
    mutationFn: () => updateUser(user._id as string, { verified: !verified }),
    onSuccess: () => {
      toast.success(t('messages.verificationStatusUpdated'));
      setIsModalOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['user', user._id] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: () => toast.error(t('clients.errors.failedToUpdateUser')),
  });

  const chips = (
    <>
      <Chip tone="neutral" className="capitalize">
        {level}
      </Chip>
      <VerificationChip user={user} />
      <Chip tone="brand">{role}</Chip>
    </>
  );

  const subtitle = [user.displayName, user.email]
    .filter(Boolean)
    .concat(
      user.createdAt ? [t('clients.detail.registeredOn', { date: formatShortDate(user.createdAt) })] : [],
    )
    .join(' · ');

  return (
    <>
      <section className="flex flex-col gap-2.5 rounded-cf-lg border border-cf-line bg-cf-surface p-4 lg:flex-row lg:flex-wrap lg:items-center lg:gap-5 lg:px-6 lg:py-[22px]">
        <div className="flex items-center gap-3 lg:contents">
          <UserAvatar
            photoURL={user.photoURL}
            initials={initialsOf(name)}
            className="h-[52px] w-[52px] text-lg lg:h-16 lg:w-16 lg:text-[22px]"
          />
          <div className="min-w-0 lg:flex-1 lg:basis-60">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="truncate text-[22px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[26px]">
                {name}
              </h1>
              <span className="hidden flex-wrap items-center gap-2.5 lg:flex">{chips}</span>
            </div>
            <p className="mt-1 truncate text-xs text-cf-ink-subtle lg:mt-1.5 lg:text-sm lg:text-cf-ink-muted">
              <span className="lg:hidden">{user.email}</span>
              <span className="hidden lg:inline">{subtitle}</span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 lg:hidden">{chips}</div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            disabled={mutation.isPending}
            className={`flex h-10 flex-1 items-center justify-center rounded-cf-md border px-3.5 text-[13px] font-semibold disabled:opacity-50 lg:flex-none lg:text-sm ${
              verified
                ? 'border-cf-danger/35 bg-cf-surface text-cf-danger hover:bg-cf-danger-tint'
                : 'border-cf-line bg-cf-surface hover:border-cf-ink'
            }`}
          >
            {verified ? t('clients.detail.unverify') : t('clients.detail.verify')}
          </button>
          {actions}
        </div>
      </section>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => mutation.mutate()}
        title={t('clients.confirmVerificationChange')}
        message={`${t('clients.confirmVerificationMessage')} ${name}`}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        isLoading={mutation.isPending}
      />
    </>
  );
}
